import { Injector } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { times } from '@sama/micro-dash';
import { InjectableSuperclass } from '@sama/ng-core';
import { merge, Observable, Subject } from 'rxjs';
import { mergeMap, share, startWith, switchMap, tap } from 'rxjs/operators';
import { BlobService } from '../../../cross-team-shared/asset-loading/blob.service';
import { AssetResource } from '../../../cross-team-shared/misc-services/resources/asset-resource.service';
import {
  FrameLoadError,
  UrlRequestService,
} from '../../../cross-team-shared/misc-services/resources/url-request.service';
import { TokenManagementService } from '../../../cross-team-shared/misc-services/token-management.service';
import { ObjectWith } from '../../../cross-team-shared/types/misc';
import {
  ShellConnector,
  shellConnectorToken,
} from '../../../team-apis/core/shell-connector';

export interface LoadedFrameInfo<FrameType> {
  sensorFrame: FrameType;
  frameNumber: number;
  retryCount: number;
}

interface LoadedFrameStats {
  loadStart: number;
  failedFrames: number;
  retriedFrames: number;
  retrievedFrames: number;
  totalFrames: number;
  totalRetryCount: number;
}

export abstract class AbstractPerFrameAssetLoadingService<
  ManifestType extends object,
  FrameType,
> extends InjectableSuperclass {
  reset$ = new Subject<void>();
  frameLoaded$: Observable<LoadedFrameInfo<FrameType> | { error: Error }>;
  manifest$ = new Subject<ManifestType>();

  protected blobService: BlobService;
  protected eventTrackingStats!: LoadedFrameStats;
  protected sanitizer: DomSanitizer;
  protected shellConnector: ShellConnector;
  protected tokenManagementService: TokenManagementService;

  private assetResource: AssetResource;
  private urlRequestService: UrlRequestService;

  constructor(injector: Injector) {
    super();
    this.assetResource = injector.get(AssetResource);
    this.blobService = injector.get(BlobService);
    this.shellConnector = injector.get(shellConnectorToken);
    this.sanitizer = injector.get(DomSanitizer);
    this.tokenManagementService = injector.get(TokenManagementService);
    this.urlRequestService = injector.get(UrlRequestService);
    this.frameLoaded$ = this.buildFrameLoaded$();
  }

  async loadManifest(manifestUrl: string) {
    const token = await this.getToken();
    return this.urlRequestService.request<ManifestType>('GET', manifestUrl, {
      headers: { Authorization: 'Bearer ' + token },
    });
  }

  protected async postProcess(asset: ArrayBuffer) {
    return asset;
  }

  protected async loadFrame(manifest: ManifestType, frameNumber: number) {
    const assets: ObjectWith<string> = {};
    let retryCount = 0;
    await Promise.all(
      this.getManifestKeys()
        .filter((key) => key in manifest)
        .map(async (key) => {
          const assetId = this.getAssetIds(manifest, key)[frameNumber];
          const loadedAsset = await this.loadAssetForFrame(
            frameNumber,
            assetId,
          );
          const asset = await this.postProcess(loadedAsset.response);
          assets[key.toString()] = this.blobService.saveToBlobStore(
            asset,
            assetId,
          );
          retryCount += loadedAsset.retryCount;
        }),
    );

    return {
      sensorFrame: this.convertToFrame(assets),
      frameNumber,
      retryCount,
    };
  }

  protected loadAssetForFrame(frameNumber: number, assetId: string) {
    return this.shellConnector.queueAssetRequest(frameNumber, async () => {
      const token = await this.tokenManagementService.getToken('assets');
      return this.assetResource.getAsset(assetId, token);
    });
  }

  protected trackAllFramesLoaded() {
    this.shellConnector.trackEvent(
      {
        id: 'load_all_per_frame_assets',
        bucket: 'untracked',
        duration: performance.now() - this.eventTrackingStats.loadStart,
        failed_frames: this.eventTrackingStats.failedFrames,
        retried_frames: this.eventTrackingStats.retriedFrames,
        retrieved_frames: this.eventTrackingStats.retrievedFrames,
        total_frames: this.eventTrackingStats.totalFrames,
        total_retry_count: this.eventTrackingStats.totalRetryCount,
      },
      { isInteractive: false },
    );
  }

  private buildFrameLoaded$() {
    return this.reset$.pipe(
      startWith(undefined),
      switchMap(() =>
        this.manifest$.pipe(
          tap((manifest) => this.initPerFrameEventTracking(manifest)),
          mergeMap((manifest) => merge(...this.loadAllFrames(manifest))),
          tap((frame) => this.trackLoadedFrame(frame)),
        ),
      ),
      share(),
    );
  }

  private loadAllFrames(manifest: ManifestType) {
    return times(this.getNumberOfFrames(manifest), async (frameNumber) =>
      this.loadFrame(manifest, frameNumber).catch((error) => ({
        error,
      })),
    );
  }

  private getToken() {
    return this.tokenManagementService.getToken('assets');
  }

  private trackLoadedFrame(
    frame: LoadedFrameInfo<FrameType> | { error: FrameLoadError },
  ) {
    if ('error' in frame) {
      ++this.eventTrackingStats.failedFrames;
      ++this.eventTrackingStats.retriedFrames;
      this.eventTrackingStats.totalRetryCount += frame.error.retryCount;
      this.trackAllFramesLoaded();
      return;
    }

    ++this.eventTrackingStats.retrievedFrames;
    if (frame.retryCount > 0) {
      this.eventTrackingStats.totalRetryCount += frame.retryCount;
      ++this.eventTrackingStats.retriedFrames;
    }

    if (
      this.eventTrackingStats.retrievedFrames ===
      this.eventTrackingStats.totalFrames
    ) {
      this.trackAllFramesLoaded();
    }
  }

  private initPerFrameEventTracking(manifest: ManifestType) {
    this.eventTrackingStats = {
      failedFrames: 0,
      loadStart: performance.now(),
      retriedFrames: 0,
      retrievedFrames: 0,
      totalFrames: this.getNumberOfFrames(manifest),
      totalRetryCount: 0,
    };
  }

  protected abstract getManifestKeys(): Array<keyof ManifestType>;

  protected abstract getAssetIds(
    manifest: ManifestType,
    manifestKey: keyof ManifestType,
  ): string[];

  protected abstract getNumberOfFrames(manifest: ManifestType): number;

  protected abstract convertToFrame(assets: ObjectWith<string>): FrameType;
}
